<template>
  <div class="matter-summary">

    <SkeletonText :loading="loading">

      <div class="packages-summary-wrapper"
           v-if="canViewPayments || hasPackages">

        <div class="summary-item"
             :class="{ 'unclickable': !canViewPayments }"
             v-for="p in packages"
             :key="p.id"
             @click="canViewPayments ? clickPackage(p): null">

          <VSvgIcon class="summary-icon"
                    tag="28" size="28" icon="package"/>

          <div class="summary-label">
            <div class="summary-name">
              {{p.name}}
            </div>
            <div class="summary-value package-value">
              <span class="package-value-balance-number">
                {{packageBalance(p)}}
              </span>
              <span class="package-value-balance-credits">
                {{packageCreditsLabel(p)}}
              </span>
            </div>
          </div>

        </div>

        <hr class="separator"
            v-if="packages.length">
      </div>

      <div class="payments-summary-wrapper"
           v-if="canViewPayments">
        <div v-if="payments && payments.paid_payments"
             class="summary-item unclickable">
          <VSvgIcon class="summary-icon"
                    tag="28" size="28" icon="hand-cash"/>
          <div class="summary-label">
            <div class="summary-name">
              {{$t('client.matters.matter_page.summary.total_payments')}}
            </div>
            <div class="summary-value">
              {{payments.paid_payments.value_h}}
            </div>
          </div>
        </div>

        <div v-if="payments && payments.open"
             class="summary-item unclickable">
          <VSvgIcon class="summary-icon"
                    tag="28" size="28" icon="pending-payments"/>
          <div class="summary-label">
            <div class="summary-name">
              {{$t('client.matters.matter_page.summary.pending_payments')}}
            </div>
            <div class="summary-value pending-payments-value">
              {{payments.open.value_h}}
            </div>
          </div>
        </div>

        <hr class="separator">
      </div>

      <div class="summary-item"
           :class="{ 'unclickable': !bookings.last }"
           @click="bookings.last ? clickBooking(bookings.last) : null">
        <VSvgIcon class="summary-icon"
                  tag="28" size="28" icon="last-appointment"/>
        <div class="summary-label">
          <div class="summary-name">
            {{$t('client.matters.matter_page.summary.last_booking')}}
          </div>
          <div class="summary-value"
               v-if="bookings.last">
            {{bookings.last.start_time | date_and_hour}}
          </div>
          <div class="summary-value"
               v-else>
            {{$t('client.matters.matter_page.summary.no_bookings')}}
          </div>
        </div>
      </div>

      <div class="summary-item"
           :class="{ 'unclickable': !bookings.upcoming }"
           @click="bookings.upcoming ? clickBooking(bookings.upcoming) : null">
        <VSvgIcon class="summary-icon"
                  tag="28" size="28" icon="upcoming-appointment"/>
        <div class="summary-label">
          <div class="summary-name">
            {{$t('client.matters.matter_page.summary.upcoming_booking')}}
          </div>
          <div class="summary-value"
               v-if="bookings.upcoming">
            {{bookings.upcoming.start_time | date_and_hour}}
          </div>
          <div class="summary-value"
               v-else>
            {{$t('client.matters.matter_page.summary.no_bookings')}}
          </div>
        </div>
      </div>

      <div class="summary-item unclickable">
        <VSvgIcon class="summary-icon"
                  tag="28" size="28" icon="noshow"/>
        <div class="summary-label">
          <div class="summary-name">
            {{$t('client.matters.matter_page.summary.number_of_no_shows')}}
          </div>
          <div class="summary-value">
            {{bookings.noShows}}
          </div>
        </div>
      </div>

    </SkeletonText>

  </div>
</template>

<script>
  import {VSvgIcon, SkeletonText} from '@/modules/common/components';
  import {packagesService} from "@/modules/common/services";
  import {mapGetters} from "vuex";

  export default {
    name: "MatterSummary",
    props: {
      matter: {
        type: Object,
        required: true
      }
    },
    components: {VSvgIcon, SkeletonText},
    data() {
      return {
        packages: [],
        loading: true
      }
    },
    async created() {
      this.$frontageApi.listen('fetch-payments', this.fetchPayments);
      this.$frontageApi.listen('fetch-bookings', this.refreshBookings);
      this.$frontageApi.listen('fetch-packages', this.fetchPackages);

      await Promise.all([
        this.fetchPayments(),
        this.fetchBookings(),
        this.hasPackages && this.fetchPackages()
      ]);

      this.loading = false;
    },
    computed: {
      ...mapGetters("accountModule", ["account"]),
      matterContact() {
        return this.matter.contacts[0].uid;
      },
      hasPackages() {
        return this.$hasFeature("booking_packages");
      },
      canViewPayments() {
        return this.$hasPermission('can_view_payments') && this.$hasFeature("payments_module");
      },
      payments() {
        return this.$store.getters['mattersModule/paymentsSummary']
      },
      bookings() {
        return this.$store.getters['mattersModule/bookingSummary']
      },
      translationFunc() {
        return this.$t.bind(this);
      }
    },
    methods: {
      async fetchPackages() {
        const {error, packages} = await packagesService.getMatterPackages(this.matterContact, this.matter.uid);

        if (error) {
          console.log(error);
          return;
        }

        this.packages = packages.filter((p) => {
          let packEndDate = new Date(p.valid_until)
          packEndDate.setDate(packEndDate.getDate()+1) // to make sure the last valid day is not considered "expired"
          return p.total_bookings - p.bookings_usage > 0 && packEndDate > new Date()
        });
      },
      fetchPayments() {
        return this.$store.dispatch({type: 'mattersModule/loadPaymentsSummary'})
      },
      fetchBookings() {
        return this.$store.dispatch({type:'mattersModule/loadMatterSummary'});
      },
      refreshBookings() {
        return this.$store.dispatch({type:'mattersModule/refreshMatterSummary'});
      },
      clickPackage(p) {
        this.$frontageApi.navigateTo('app.client-package.show', {uid: p.id, origin: 'Client activity highlights'});
      },
      clickBooking(booking) {
        const route = booking.type === 'appointment' ? 'app.appointment.show' : 'app.event';
        this.$frontageApi.navigateTo(route, {uid: booking.uid, origin: 'Client activity highlights'});
      },
      clickPayments(state) {
        this.$frontageApi.send('matter_action', {action: 'filter_payments', state: state});
      },
      packageBalance(p) {
        return p.total_bookings - p.bookings_usage;
      },
      packageCreditsLabel(p) {
        return p.total_bookings - p.bookings_usage === 1 ? this.$t('client.matters.matter_page.summary.credit') : this.$t('client.matters.matter_page.summary.credits');
      }
    },
    destroyed() {
      this.$frontageApi.remove('fetch-payments', this.fetchPayments);
      this.$frontageApi.remove('fetch-bookings', this.refreshBookings);
      this.$frontageApi.remove('fetch-packages', this.fetchPackages);
    }
  }
</script>

<style lang="scss" scoped>
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_mixins.scss";

  .matter-summary {
    margin: 0 -9px;

    .separator {
      border: none;
      height: 1px;
      background-color: $grey-color-2;
      margin: 16px 0;
    }

    ::v-deep .summary-item {
      display: flex;
      padding: 0 8px;
      transition: background-color 0.2s;
      align-items: center;

      &:not(:last-child) {
        margin-bottom: 16px;
      }

      &:not(.unclickable) {
        cursor: pointer;

        &:hover {
          background-color: $grey-color-10;
        }
      }

      .summary-label {
        @include i-margin-mirror(16px, 0);
        line-height: 20px;

        .summary-name {
          color: $color_dark_sec_text;
          font-size: 13px;
        }

        .summary-value {
          font-size: 15px;
          font-weight: 500;

          .package-value {

            &-balance-number {
              @include i-margin-mirror(0, 2px);
            }

            &-balance-credits {
              font-size: 12px;
              color: $color_dark_sec_text;
            }
          }
        }
      }
    }
  }

  .package-tooltip {
    font-family: var(--primary-font-family);
    font-size: 12px;
    font-weight: 500;
    padding: 2px 10px;
  }
</style>
